import { createSystem, defaultConfig, defineRecipe, defineConfig } from '@chakra-ui/react';
import useWorkspace from '@/lib/hooks/use-workspace';
import { useMemo } from 'react';
import { getContrastColor, adjustColorLightness } from '@/lib/color-utils';

export const containerRecipe = defineRecipe({
  base: {
    px: { base: 0, md: '6', lg: '8' },
  },
});

const defaultColors = {
  primary: '#18181b',
  secondary: '#f7f5f2',
};

export function useWorkspaceTheme() {
  const { workspace } = useWorkspace();

  const workspaceColors = useMemo(() => {
    return {
      primary: workspace?.primary_color || defaultColors.primary,
      secondary: workspace?.secondary_color || defaultColors.secondary,
    };
  }, [workspace?.primary_color, workspace?.secondary_color]);

  const primaryContrastColor = useMemo(
    () => getContrastColor(workspaceColors.primary),
    [workspaceColors.primary],
  );

  const secondaryContrastColor = useMemo(
    () => getContrastColor(workspaceColors.primary),
    [workspaceColors.primary],
  );

  const customConfig = useMemo(
    () =>
      defineConfig({
        theme: {
          recipes: {
            container: containerRecipe,
          },
          tokens: {
            colors: {
              brand: {
                50: { value: adjustColorLightness(workspaceColors.primary, 0.9) },
                100: { value: adjustColorLightness(workspaceColors.primary, 0.8) },
                200: { value: adjustColorLightness(workspaceColors.primary, 0.6) },
                300: { value: adjustColorLightness(workspaceColors.primary, 0.4) },
                400: { value: adjustColorLightness(workspaceColors.primary, 0.2) },
                500: { value: workspaceColors.primary },
                600: { value: adjustColorLightness(workspaceColors.primary, -0.1) },
                700: { value: adjustColorLightness(workspaceColors.primary, -0.2) },
                800: { value: adjustColorLightness(workspaceColors.primary, -0.3) },
                900: { value: adjustColorLightness(workspaceColors.primary, -0.4) },
              },
              secondary: {
                50: { value: adjustColorLightness(workspaceColors.secondary, 0.9) },
                100: { value: adjustColorLightness(workspaceColors.secondary, 0.8) },
                200: { value: adjustColorLightness(workspaceColors.secondary, 0.6) },
                300: { value: adjustColorLightness(workspaceColors.secondary, 0.4) },
                400: { value: adjustColorLightness(workspaceColors.secondary, 0.2) },
                500: { value: workspaceColors.secondary },
                600: { value: adjustColorLightness(workspaceColors.secondary, -0.1) },
                700: { value: adjustColorLightness(workspaceColors.secondary, -0.2) },
                800: { value: adjustColorLightness(workspaceColors.secondary, -0.3) },
                900: { value: adjustColorLightness(workspaceColors.secondary, -0.4) },
              },
            },
          },
          semanticTokens: {
            colors: {
              brand: {
                solid: { value: '{colors.brand.600}' },
                contrast: {
                  value: primaryContrastColor,
                },
                fg: {
                  value:
                    primaryContrastColor === '#000000'
                      ? '{colors.brand.800}'
                      : '{colors.brand.700}',
                },
                muted: { value: '{colors.brand.200}' },
                subtle: { value: '{colors.brand.100}' },
                emphasized: { value: '{colors.brand.300}' },
                focusRing: { value: '{colors.brand.500}' },
              },
              secondary: {
                solid: { value: '{colors.secondary.600}' },
                contrast: {
                  value: secondaryContrastColor,
                },
                fg: {
                  value:
                    secondaryContrastColor === '#000000'
                      ? '{colors.secondary.800}'
                      : '{colors.secondary.700}',
                },
                muted: { value: '{colors.secondary.200}' },
                subtle: { value: '{colors.secondary.100}' },
                emphasized: { value: '{colors.secondary.300}' },
                focusRing: { value: '{colors.secondary.500}' },
              },
            },
          },
        },
      }),
    [
      workspaceColors.primary,
      primaryContrastColor,
      workspaceColors.secondary,
      secondaryContrastColor,
    ],
  );

  const system = useMemo(() => createSystem(defaultConfig, customConfig), [customConfig]);

  return system;
}

const staticDefaultSystem = createSystem(
  defaultConfig,
  defineConfig({
    theme: {
      recipes: {
        container: containerRecipe,
      },
    },
  }),
);

export { staticDefaultSystem as defaultSystem };
